/**
 * Derivações puras da Tela 2 (Pessoas) — wire → view model.
 *
 * Concentra TODA decisão de "como exibir o dado real" para ser testável sem
 * React. Honestidade é regra (memória `feedback_anotacoes_prototipo_nao_sao_copy_producao`):
 *   - D1: o backend dá `riskScore` (number 0..100), NÃO um nível de classificação
 *     1-4. Derivamos uma FAIXA de risco honesta — nunca um "N2" fabricado.
 *   - D2/D3: stats de conversas/appointments são derivados do conjunto CARREGADO
 *     (a paginação não traz total), com rótulo honesto ("carregadas").
 *   - Valores legados/desconhecidos degradam para o cru, nunca renderizam vazio.
 */

import { BOND_TYPES_BY_ID } from '@lume/shared';

import type {
  PersonBond,
  PersonBondRole,
  PersonGuardianship,
  PersonLegalCapacity,
  PersonConversation,
  PersonAppointment,
} from './types';
import type { BondType, BondStatus } from '@lume/shared';

// ─── D1: riskScore → faixa de risco honesta ─────────────────────────────

export type RiskLevel = 'baixo' | 'medio' | 'alto' | 'critico';

export interface RiskBand {
  level: RiskLevel;
  label: string;
}

/**
 * `Person.riskScore` é Decimal(5,2) na escala 0..100 (schema). Mapeamos em 4
 * faixas. NÃO é a sensibilidade LGPD N1-4 (essa não existe no contrato de
 * Person) — por isso o rótulo é "Risco", não "Classificação".
 */
export function riskBand(score: number): RiskBand {
  const s = Number.isFinite(score) ? score : 0;
  if (s >= 75) return { level: 'critico', label: 'Risco crítico' };
  if (s >= 50) return { level: 'alto', label: 'Risco alto' };
  if (s >= 25) return { level: 'medio', label: 'Risco médio' };
  return { level: 'baixo', label: 'Risco baixo' };
}

// ─── Bonds ──────────────────────────────────────────────────────────────

export type BondVisualGroup = 'commercial' | 'legal' | 'care' | 'property' | 'public';

/**
 * Mapeia os 21 `BondType` canônicos (ADR-037) nos 5 grupos visuais do
 * `.bond-badge--*` do DS. Todo tipo tem grupo (verificado no teste contra
 * `BOND_TYPES`).
 */
const BOND_VISUAL_GROUP: Record<BondType, BondVisualGroup> = {
  contract: 'commercial',
  'customer-account': 'commercial',
  opportunity: 'commercial',
  procurement: 'commercial',
  project: 'commercial',
  partnership: 'commercial',
  'vendor-relationship': 'commercial',
  interest: 'commercial',
  'purchase-intent': 'commercial',
  'transaction-attempt': 'commercial',
  'rental-contract': 'commercial',
  'legal-case': 'legal',
  mediation: 'legal',
  'compliance-case': 'legal',
  'patient-care': 'care',
  student: 'care',
  'social-care-case': 'care',
  'home-care': 'care',
  property: 'property',
  vehicle: 'property',
  equipment: 'property',
};

export function bondVisualGroup(type: BondType): BondVisualGroup {
  return BOND_VISUAL_GROUP[type] ?? 'public';
}

/**
 * Rótulo humano do bond. O contrato não tem um "label" — derivamos de
 * `metadata.customerName`/`contractNumber` (mesma fonte usada no serializer de
 * ConflictAlert) e caímos para o label canônico do tipo, depois o tipo cru.
 */
export function bondLabel(bond: Pick<PersonBond, 'type' | 'metadata'>): string {
  const meta = bond.metadata as { customerName?: unknown; contractNumber?: unknown };
  if (typeof meta.customerName === 'string' && meta.customerName !== '') return meta.customerName;
  if (typeof meta.contractNumber === 'string' && meta.contractNumber !== '') {
    return `Contrato ${meta.contractNumber}`;
  }
  return BOND_TYPES_BY_ID[bond.type]?.label ?? bond.type;
}

const BOND_ROLE_LABEL: Record<PersonBondRole, string> = {
  participant: 'Participante',
  'authorized-contact': 'Contato autorizado',
  'related-person': 'Pessoa relacionada',
};

export function bondRolesLabel(roles: PersonBondRole[]): string {
  if (roles.length === 0) return 'Sem papel';
  return roles.map((r) => BOND_ROLE_LABEL[r] ?? r).join(' · ');
}

const BOND_STATUS_LABEL: Record<BondStatus, string> = {
  active: 'ativo',
  paused: 'pausado',
  closed: 'encerrado',
  archived: 'arquivado',
};

export function bondStatusLabel(status: BondStatus): string {
  return BOND_STATUS_LABEL[status] ?? status;
}

export function bondIsAdversarial(bond: Pick<PersonBond, 'adversarialMode'>): boolean {
  return bond.adversarialMode !== 'none';
}

// ─── Autonomia de agenda por-cliente (Frente B Sessão 3) ────────────────────

const NATIVE_SCHEDULING_MODE_LABEL: Record<string, string> = {
  propose_and_book: 'IA agenda sozinha',
  propose_only: 'IA propõe; humano confirma',
  qualify_only: 'IA só qualifica',
};

/**
 * Rótulo da autonomia de agenda do cliente neste bond. `null`/desconhecido → null
 * (sem override explícito → herda o cascade; nada a exibir).
 */
export function nativeSchedulingModeLabel(mode: string | null | undefined): string | null {
  if (mode === null || mode === undefined) return null;
  return NATIVE_SCHEDULING_MODE_LABEL[mode] ?? mode;
}

// ─── Política de atuação da IA por-cliente (S4 PR-b, Item 404 faceta b) ─────

const AI_ENGAGEMENT_MODE_LABEL: Record<string, string> = {
  no_engage: 'IA não atua com este cliente',
  // 'welcome_only' reservado (pendente-de-choke, design §3-bis) — NÃO oferecido
  // no editor ainda; o rótulo existe só p/ degradar honesto se aparecer via
  // seed/import/SQL direto (o motor o trata como 'engage').
  welcome_only: 'IA só acolhe e passa (em breve)',
  engage: 'IA atua normalmente',
};

/**
 * Rótulo da política de atuação da IA para este cliente neste bond.
 * `null`/desconhecido → null (sem override explícito → herda `'engage'`).
 */
export function aiEngagementModeLabel(mode: string | null | undefined): string | null {
  if (mode === null || mode === undefined) return null;
  return AI_ENGAGEMENT_MODE_LABEL[mode] ?? mode;
}

// ─── Identidades multi-canal ────────────────────────────────────────────

const CHANNEL_LABEL: Record<string, string> = {
  whatsapp_cloud_api: 'WhatsApp',
  'whatsapp-cloud-api': 'WhatsApp',
  telegram: 'Telegram',
  email: 'E-mail',
  webchat: 'Webchat',
  instagram_dm: 'Instagram',
  'instagram-dm': 'Instagram',
  // `Record<string, …>` não é exaustivo — sem esta linha cai no `?? channel` e a linha de
  // identidade da pessoa renderiza `simulador` cru, minúsculo. Mesma forma nas duas grafias.
  simulador: 'Simulador',
};

export function channelLabel(channel: string): string {
  return CHANNEL_LABEL[channel] ?? channel;
}

// ─── Guardianship (ADR-114) ─────────────────────────────────────────────

export type GuardianshipIndicatorState = 'none' | 'minor' | 'ward' | 'expired' | 'pending';

export interface GuardianshipIndicator {
  state: GuardianshipIndicatorState;
  label: string;
  source: PersonGuardianship | null;
}

const GUARDIANSHIP_TYPE_LABEL: Record<string, string> = {
  'legal-guardian': 'Responsável legal',
  curator: 'Curatela',
  'authorized-caregiver': 'Cuidador autorizado',
  temporary: 'Representação temporária',
  'emergency-contact': 'Contato de emergência',
};

const GUARDIANSHIP_BASIS_LABEL: Record<string, string> = {
  'court-order': 'ordem judicial',
  parental: 'pátrio poder',
  'attorney-power': 'procuração',
  'declared-by-titular': 'declarado pelo titular',
  'emergency-protocol': 'protocolo de emergência',
};

/**
 * Deriva o indicador de guardianship a partir das tutelas em que a pessoa é o
 * PUPILO (`guardianshipsAsWard`). Precedência: ativa > pendente (suspensa) >
 * vencida. Curatela → `ward`; demais tipos ativos → `minor`. Não fabricamos o
 * nome do responsável (o contrato dá só `guardianId`) — o rótulo é honesto a
 * partir do tipo + base legal.
 */
export function deriveGuardianship(asWard: PersonGuardianship[]): GuardianshipIndicator {
  const live = asWard.filter((g) => g.status !== 'revoked');
  if (live.length === 0) return { state: 'none', label: 'Sem guardianship', source: null };

  const active = live.find((g) => g.status === 'active');
  const suspended = live.find((g) => g.status === 'suspended');
  const expired = live.find((g) => g.status === 'expired');
  const chosen = active ?? suspended ?? expired ?? live[0];
  if (chosen === undefined) return { state: 'none', label: 'Sem guardianship', source: null };

  const typeLabel = GUARDIANSHIP_TYPE_LABEL[chosen.type] ?? chosen.type;
  const basisLabel = chosen.basis !== null ? GUARDIANSHIP_BASIS_LABEL[chosen.basis] : undefined;
  const label = basisLabel !== undefined ? `${typeLabel} · ${basisLabel}` : typeLabel;

  if (chosen.status === 'expired') return { state: 'expired', label, source: chosen };
  if (chosen.status === 'suspended') return { state: 'pending', label, source: chosen };
  // active
  const state: GuardianshipIndicatorState = chosen.type === 'curator' ? 'ward' : 'minor';
  return { state, label, source: chosen };
}

// ─── Capacidade jurídica (Regulated) ────────────────────────────────────

const LEGAL_CAPACITY_STATUS_LABEL: Record<string, string> = {
  full: 'plena',
  limited: 'limitada',
  none: 'sem capacidade',
  'age-restricted': 'restrita por idade',
};

const LEGAL_CAPACITY_BASIS_LABEL: Record<string, string> = {
  age: 'idade',
  cognitive: 'cognitiva',
  'curatela-judicial': 'curatela judicial',
  temporal: 'temporal',
  'age-progressive': 'idade progressiva',
};

export function legalCapacityLabel(lc: PersonLegalCapacity): string {
  const status = LEGAL_CAPACITY_STATUS_LABEL[lc.status] ?? lc.status;
  const basis = lc.basis !== null ? LEGAL_CAPACITY_BASIS_LABEL[lc.basis] : undefined;
  return basis !== undefined ? `${status} · ${basis}` : status;
}

// ─── D2: stats de conversas (do conjunto carregado) ─────────────────────

// O endpoint de conversas-da-pessoa serializa o status CRU do Prisma
// (snake_case: `in_progress`, `awaiting_customer`, `closed_unresolved`…).
// Exportado: fonte ÚNICA de «terminal» para quem classifica episódios
// (EpisodeTrail — F.5 C1: uma segunda lista à mão nasceu na convenção errada).
export const TERMINAL_CONVERSATION_STATUSES = new Set([
  'closed',
  'resolved',
  'closed_unresolved',
  'archived',
]);

export interface ConversationStats {
  loaded: number;
  active: number;
}

export function conversationStats(convs: PersonConversation[]): ConversationStats {
  const active = convs.filter((c) => !TERMINAL_CONVERSATION_STATUSES.has(c.status)).length;
  return { loaded: convs.length, active };
}

const CONVERSATION_STATUS_LABEL: Record<string, string> = {
  new: 'nova',
  assigned: 'atribuída',
  in_progress: 'em atendimento',
  awaiting_customer: 'aguardando cliente',
  awaiting_internal_action: 'aguardando ação interna',
  awaiting_temporal: 'aguardando prazo',
  awaiting_attendant: 'aguardando você',
  paused: 'pausada',
  closed: 'concluída',
  reopened: 'reaberta',
  resolved: 'resolvida',
  closed_unresolved: 'encerrada sem resolução',
  archived: 'arquivada',
};

export function conversationStatusLabel(status: string): string {
  return CONVERSATION_STATUS_LABEL[status] ?? status;
}

/** F7 — rótulo NEUTRO do desfecho (ConversationOutcomeType) na timeline do operador. */
const CONVERSATION_OUTCOME_LABEL: Record<string, string> = {
  resolved: 'Resolvida',
  unresolved_abandoned_by_customer: 'Abandonada pelo cliente',
  unresolved_attendant_gave_up: 'Sem resolução',
  unresolved_blocked_by_system: 'Bloqueada pelo sistema',
  unresolved_out_of_scope: 'Fora de escopo',
  not_applicable: 'Não aplicável',
};

export function conversationOutcomeLabel(outcomeType: string): string {
  return CONVERSATION_OUTCOME_LABEL[outcomeType] ?? outcomeType;
}

// ─── D3: stats de appointments (do conjunto carregado) ──────────────────

export interface AppointmentStats {
  upcoming: number;
  completed: number;
  rescheduled: number;
  noShow: number;
}

const UPCOMING_STATUSES = new Set(['scheduled', 'confirmed', 'imminent']);

export function appointmentStats(appts: PersonAppointment[], now: Date): AppointmentStats {
  let upcoming = 0;
  let completed = 0;
  let rescheduled = 0;
  let noShow = 0;
  const nowMs = now.getTime();
  for (const a of appts) {
    if (a.status === 'completed') completed += 1;
    else if (a.status === 'rescheduled') rescheduled += 1;
    else if (a.status === 'no_show') noShow += 1;
    else if (UPCOMING_STATUSES.has(a.status) && new Date(a.scheduledAt).getTime() >= nowMs) {
      upcoming += 1;
    }
  }
  return { upcoming, completed, rescheduled, noShow };
}

const APPOINTMENT_STATUS_LABEL: Record<string, string> = {
  scheduled: 'agendado',
  confirmed: 'confirmado',
  imminent: 'iminente',
  completed: 'concluído',
  cancelled: 'cancelado',
  no_show: 'não compareceu',
  rescheduled: 'reagendado',
};

export function appointmentStatusLabel(status: string): string {
  return APPOINTMENT_STATUS_LABEL[status] ?? status;
}

/**
 * CPF mascarado para exibição read-only (design Brief 2B: "123.***.***-04" —
 * privacidade desde o desenho, P15). Valor que não é CPF volta intacto.
 */
export function maskCpf(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 11 || /\D/.test(value.replace(/[.-]/g, ''))) return value;
  return `${digits.slice(0, 3)}.***.***-${digits.slice(9)}`;
}

const GUARDIANSHIP_STATUS_LABEL: Record<string, string> = {
  active: 'ativa',
  expired: 'expirada',
  pending: 'pendente',
  revoked: 'revogada',
  suspended: 'suspensa',
};

export function guardianshipStatusLabel(status: string): string {
  return GUARDIANSHIP_STATUS_LABEL[status] ?? status;
}

export type BadgeVariant = 'success' | 'danger' | 'warning' | 'neutral';

export function appointmentBadgeVariant(status: string): BadgeVariant {
  if (status === 'confirmed') return 'success';
  if (status === 'no_show' || status === 'cancelled') return 'danger';
  if (status === 'completed') return 'neutral';
  return 'warning';
}

// ─── Formatação ─────────────────────────────────────────────────────────

export function personInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase();
  const first = parts[0] ?? '';
  const last = parts[parts.length - 1] ?? '';
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

/** Data curta pt-BR (ex.: 14/mai/2026). ISO inválida degrada para a string. */
export function formatShortDate(iso: string | null): string {
  if (iso === null || iso === '') return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(d);
}

export function formatDateTime(iso: string | null): string {
  if (iso === null || iso === '') return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}
